import {useRouter} from "next/router";
import {useEffect, useState} from "react";
import {normalizePluginsListUrl, readPluginsListState} from "@utils/pluginsListState";

/**
 * React hook that forces a scroll reset to a particular set of coordinates in the document
 * after `next/router` finishes transitioning to a new page client side. It smoothly scrolls back to
 * the top by default.
 *
 * @see https://github.com/vercel/next.js/issues/3249
 * @see https://github.com/vercel/next.js/issues/15206
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ScrollToOptions
 * @param {ScrollOptions} [options={}] Hook options.
 * @param {ScrollBehavior} [options.behavior='smooth'] Specifies whether the scrolling should animate smoothly,
 *  or happen instantly in a single jump.
 * @param {number} [options.left=0] Specifies the number of pixels along the X axis to scroll the window.
 * @param {number} [options.top=0] Specifies the number of pixels along the Y axis to scroll the window.
 */
export const useRouterScroll = ({behavior = 'auto', left = 0, top = 0} = {}) => {
    const router = useRouter();
    useEffect(() => {
            const handleRouteChangeComplete = (url: string, {shallow}: { shallow: boolean }) => {
                if (!shallow) {
                    const stored = readPluginsListState();
                    if (stored && normalizePluginsListUrl(stored.url) === normalizePluginsListUrl(url)) {
                        return;
                    }
                    const target = document.querySelector('#app-body');
                    const hasScrollableContainer = target && target instanceof HTMLElement && target.scrollHeight > target.clientHeight;

                    if (hasScrollableContainer) {
                        target.scrollTo({top, left, behavior: behavior as ScrollBehavior});
                    } else if (typeof window !== 'undefined') {
                        window.scrollTo({top, left, behavior: behavior as ScrollBehavior});
                    }
                }
            }

            router.events.on('routeChangeComplete', handleRouteChangeComplete);
            return () => {
                router.events.off('routeChangeComplete', handleRouteChangeComplete);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [behavior, left, top]
    )
    ;
}


export const useLoadingAnimationOnPageChange = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [loadingTimeout, setLoadingTimeout] = useState<NodeJS.Timeout | null>(null);
    const router = useRouter();

    const triggerLoading = (url: string, {shallow}: { shallow: boolean }) => {
        if (!shallow) {
            if (loadingTimeout) {
                clearTimeout(loadingTimeout);
            }
            setIsLoading(true);
            setLoadingTimeout(setTimeout(() => {
                setIsLoading(false);
            }, 5000));
        }
    }

    const stopLoadingOnComplete = (url: string, {shallow}: { shallow: boolean }) => {
        if (loadingTimeout) {
            clearTimeout(loadingTimeout);
        }
        setIsLoading(false);
    }

    const stopLoadingOnError = (err: Error, url: string, {shallow}: { shallow: boolean }) => {
        if (loadingTimeout) {
            clearTimeout(loadingTimeout);
        }
        setIsLoading(false);
    }

    useEffect(() => {
        if (typeof window !== 'undefined') {

            router.events.on('routeChangeStart', triggerLoading);
            router.events.on('routeChangeComplete', stopLoadingOnComplete);
            router.events.on('routeChangeError', stopLoadingOnError);
            return () => {
                window.onbeforeunload = null;
                router.events.off('routeChangeStart', triggerLoading);
                router.events.off('routeChangeComplete', stopLoadingOnComplete);
                router.events.off('routeChangeError', stopLoadingOnError);
            }
        }
    }, []);

    return isLoading;
}
